(() => {
  'use strict';

  const COLLECTION_SELECTOR =
    'collection-component[data-pagination-type="infinite"], collection-component[data-pagination-type="load_more"]';
  const PRODUCTS_CONTAINER_SELECTOR = '[data-products-grid]';
  const PRODUCT_GRID_SELECTOR = '#CollectionLoop';
  const TRIGGER_SELECTOR = '[data-infinite-scroll-trigger]';
  const BUTTON_SELECTOR = '[data-load-more-button]';


  // BUCKS Currency Converter: apply the selected currency to AJAX-inserted prices.
  // Official BUCKS integration API: window.bucksCC.reConvert().
  function reconvertBucksPrices() {
    const bucks = window.bucksCC;
    if (!bucks || typeof bucks.reConvert !== 'function') return;
    try {
      bucks.reConvert();
    } catch (error) {
      // Currency app errors must not interrupt collection pagination.
      console.warn('BUCKS currency conversion failed:', error);
    }
  }

  class CollectionInfiniteScroll {
    constructor(container) {
      this.container = container;
      this.productsContainer = container.querySelector(
        PRODUCTS_CONTAINER_SELECTOR
      );

      this.loading = false;
      this.intersectionObserver = null;
      this.mutationObserver = null;

      // Original infinite-scroll behaviour remains unchanged.
      this.isInfinite = container.dataset.paginationType === 'infinite';
      this.requestController = null;
      this.onLoadMoreClick = (event) => {
        const button = event.target.closest(BUTTON_SELECTOR);
        if (!button || !this.container.contains(button)) return;
        event.preventDefault();
        this.loadNextPage();
      };

      if (!this.productsContainer) return;

      // Delegation survives Shopify filter/sort HTML replacement.
      this.container.addEventListener('click', this.onLoadMoreClick);
      this.watchForFilterAndSortUpdates();
      this.observeTrigger();
    }

    watchForFilterAndSortUpdates() {
      this.mutationObserver = new MutationObserver((mutations) => {
        const gridWasReplaced = mutations.some(
          (mutation) => mutation.type === 'childList'
        );

        if (!gridWasReplaced) return;

        // Cancel an old-page request when filters/sort replace the product grid.
        if (this.requestController) {
          this.requestController.abort();
          this.requestController = null;
        }
        this.loading = false;

        this.container.classList.remove('is-loading-more', 'has-load-error');
        this.container.removeAttribute('data-infinite-complete');

        this.observeTrigger();
        reconvertBucksPrices();
      });

      /*
        Filter / Sort JS [data-products-grid]
        ke direct children replace karta hai.

        Isliye sirf childList observe kar rahe hain.
      */
      this.mutationObserver.observe(this.productsContainer, {
        childList: true
      });
    }

    getTrigger() {
      return this.productsContainer.querySelector(TRIGGER_SELECTOR);
    }

    getLoadMoreButton() {
      return this.productsContainer.querySelector(BUTTON_SELECTOR);
    }

    setLoadMoreText(label) {
      const labelElement = this.productsContainer.querySelector('[data-load-more-label]');
      if (labelElement) labelElement.textContent = label;
    }

    observeTrigger() {
      if (this.intersectionObserver) {
        this.intersectionObserver.disconnect();
      }

      const trigger = this.getTrigger();
      const nextUrl = trigger ? trigger.dataset.nextUrl : '';

      // For Load More, show a button instead of attaching a scroll observer.
      const loadMoreWrap = this.productsContainer.querySelector('[data-load-more-wrap]');
      if (loadMoreWrap) loadMoreWrap.hidden = !nextUrl;

      /*
        Agar next page nahi hai,
        infinite scroll complete.
      */
      if (!trigger || !nextUrl) {
        this.container.setAttribute(
          'data-infinite-complete',
          'true'
        );

        return;
      }

      this.container.removeAttribute(
        'data-infinite-complete'
      );

      // Infinite Scroll still uses the original IntersectionObserver.
      // Load More waits exclusively for a button click.
      if (!this.isInfinite) return;

      this.intersectionObserver =
        new IntersectionObserver(
          (entries) => {
            const isVisible = entries.some(
              (entry) => entry.isIntersecting
            );

            if (isVisible) {
              this.loadNextPage();
            }
          },
          {
            root: null,

            /*
              User bottom tak exact aaye usse pehle
              next products load start ho jayenge.
            */
            rootMargin: '700px 0px',

            threshold: 0
          }
        );

      this.intersectionObserver.observe(trigger);
    }

    async loadNextPage() {
      /*
        Double request prevent.
      */
      if (this.loading) return;

      const trigger = this.getTrigger();

      const nextUrl = trigger
        ? trigger.dataset.nextUrl
        : '';

      if (!trigger || !nextUrl) {
        this.observeTrigger();
        return;
      }

      // Capture the current grid so an older response never enters a new filter result.
      const requestedGrid = this.productsContainer.querySelector(PRODUCT_GRID_SELECTOR);
      const requestController = new AbortController();
      this.requestController = requestController;
      this.loading = true;
      this.container.classList.remove('has-load-error');
      trigger.removeAttribute('data-infinite-error');

      const button = this.getLoadMoreButton();
      const message = this.productsContainer.querySelector('[data-load-more-message]');
      if (button) button.disabled = true;
      if (message) message.textContent = '';
      this.setLoadMoreText('Loading...');

      this.container.classList.add(
        'is-loading-more'
      );

      trigger.setAttribute(
        'aria-busy',
        'true'
      );

      try {
        /*
          Next collection page fetch.
        */
        const response = await fetch(
          nextUrl,
          {
            credentials: 'same-origin',
            signal: requestController.signal,

            headers: {
              'X-Requested-With':
                'XMLHttpRequest'
            }
          }
        );

        if (!response.ok) {
          throw new Error(
            `Infinite scroll request failed: ${response.status}`
          );
        }

        const html =
          await response.text();

        if (requestController.signal.aborted || this.requestController !== requestController) return;

        /*
          Returned page ko HTML document me convert.
        */
        const documentFromResponse =
          new DOMParser().parseFromString(
            html,
            'text/html'
          );

        /*
          Same collection section find.
        */
        const nextCollection =
          documentFromResponse.getElementById(
            this.container.id
          );

        /*
          Next page product grid.
        */
        const nextGrid =
          nextCollection &&
          nextCollection.querySelector(
            PRODUCT_GRID_SELECTOR
          );

        /*
          Next page infinite trigger.
        */
        const nextTrigger =
          nextCollection &&
          nextCollection.querySelector(
            TRIGGER_SELECTOR
          );

        /*
          Current product grid.
        */
        const currentGrid =
          this.productsContainer.querySelector(
            PRODUCT_GRID_SELECTOR
          );

        if (!nextGrid || !currentGrid || !nextTrigger) {
          throw new Error(
            'Infinite scroll could not find the product grid or next-page trigger.'
          );
        }
        if (currentGrid !== requestedGrid || this.getTrigger() !== trigger) return;
        if (nextTrigger.dataset.nextUrl && nextTrigger.dataset.nextUrl === nextUrl) {
          throw new Error('Next page URL did not advance.');
        }

        /*
          Next page products ko current grid
          ke end me append karna.
        */
        const fragment =
          document.createDocumentFragment();

        Array.from(
          nextGrid.children
        ).forEach((item) => {
          fragment.appendChild(
            document.importNode(
              item,
              true
            )
          );
        });

        currentGrid.appendChild(
          fragment
        );

        // Fetch HTML is not processed by the BUCKS app until explicitly re-converted.
        reconvertBucksPrices();

        /*
          Next page URL update.
        */
        trigger.dataset.nextUrl =
          nextTrigger
            ? nextTrigger.dataset.nextUrl || ''
            : '';

        trigger.dataset.currentPage =
          nextTrigger
            ? nextTrigger.dataset.currentPage || ''
            : '';

        /*
          Custom event:
          future me product cards ke kisi
          extra JS ko reinitialize karna ho
          to is event ko use kar sakte hain.
        */
        document.dispatchEvent(
          new CustomEvent(
            this.isInfinite ? 'theme:collection:infinite-loaded' : 'theme:collection:load-more-loaded',
            {
              bubbles: true,

              detail: {
                collection:
                  this.container,

                url:
                  nextUrl,

                page:
                  trigger.dataset.currentPage
              }
            }
          )
        );
      } catch (error) {
        if (error.name !== 'AbortError') {
          console.error(error);
          trigger.dataset.infiniteError = 'true';
          this.container.classList.add('has-load-error');
          if (message) message.textContent = 'Unable to load products. Please try again.';
        }
      } finally {
        // Filter/sort updates may have cancelled this particular request.
        if (this.requestController !== requestController) return;
        this.requestController = null;
        this.loading = false;

        this.container.classList.remove(
          'is-loading-more'
        );

        trigger.removeAttribute(
          'aria-busy'
        );

        if (button) button.disabled = false;
        this.setLoadMoreText(this.isInfinite ? 'Retry Loading' : 'Load More');

        /*
          Agar error nahi hai to
          next page observe karo.

          Agar newly loaded products ke baad bhi
          viewport ka bottom nearby hai,
          next page automatically load ho sakta hai.
        */
        if (
          !trigger.dataset.infiniteError
        ) {
          this.observeTrigger();
        }
      }
    }

    destroy() {
      if (
        this.intersectionObserver
      ) {
        this.intersectionObserver.disconnect();
      }

      if (
        this.mutationObserver
      ) {
        this.mutationObserver.disconnect();
      }
      if (this.requestController) this.requestController.abort();
      this.container.removeEventListener('click', this.onLoadMoreClick);
    }
  }

  /*
    Single collection initialize.
  */
  function initCollection(
    container
  ) {
    if (
      !container ||
      container.__infiniteScrollInstance
    ) {
      return;
    }

    container.__infiniteScrollInstance =
      new CollectionInfiniteScroll(
        container
      );
  }

  /*
    Page par jitni infinite-scroll
    collections hain initialize.
  */
  function initAll(
    root = document
  ) {
    root
      .querySelectorAll(
        COLLECTION_SELECTOR
      )
      .forEach(
        initCollection
      );
  }

  /*
    Initial page load.
  */
  if (
    document.readyState ===
    'loading'
  ) {
    document.addEventListener(
      'DOMContentLoaded',
      () => initAll()
    );
  } else {
    initAll();
  }

  /*
    Shopify Theme Editor:
    section reload support.
  */
  document.addEventListener(
    'shopify:section:load',
    (event) => {
      const section =
        event.target;

      if (!section) return;

      if (
        section.matches &&
        section.matches(
          COLLECTION_SELECTOR
        )
      ) {
        initCollection(
          section
        );
      } else {
        initAll(
          section
        );
      }
    }
  );

  /*
    Shopify Theme Editor:
    section unload cleanup.
  */
  document.addEventListener(
    'shopify:section:unload',
    (event) => {
      const section =
        event.target;

      if (!section) return;

      const containers = [];

      if (
        section.matches &&
        section.matches(
          COLLECTION_SELECTOR
        )
      ) {
        containers.push(
          section
        );
      }

      if (
        section.querySelectorAll
      ) {
        containers.push(
          ...section.querySelectorAll(
            COLLECTION_SELECTOR
          )
        );
      }

      containers.forEach(
        (container) => {
          if (
            container.__infiniteScrollInstance
          ) {
            container
              .__infiniteScrollInstance
              .destroy();

            delete container
              .__infiniteScrollInstance;
          }
        }
      );
    }
  );
})();
